import { jsPDF as JsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

let cachedAmiriFontBase64: string | null = null;

/**
 * Fetches the Amiri Regular font and converts it to a Base64 string.
 * @returns {Promise<string | null>} The base64 encoded font or null on failure.
 */
export const getAmiriFontBase64 = async (): Promise<string | null> => {
  if (cachedAmiriFontBase64) {
    return cachedAmiriFontBase64;
  }

  try {
    const fontUrl = `${window.location.origin}/fonts/Amiri-Regular.ttf`;
    const response = await fetch(fontUrl);
    if (!response.ok) {
      const fallbackResponse = await fetch('/fonts/Amiri-Regular.ttf');
      const buffer = await fallbackResponse.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const length = bytes.byteLength;
      for (let index = 0; index < length; index += 1) {
        binary += String.fromCharCode(bytes[index]);
      }
      cachedAmiriFontBase64 = window.btoa(binary);
      return cachedAmiriFontBase64;
    }

    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const length = bytes.byteLength;
    for (let index = 0; index < length; index += 1) {
      binary += String.fromCharCode(bytes[index]);
    }
    cachedAmiriFontBase64 = window.btoa(binary);
    return cachedAmiriFontBase64;
  } catch (error) {
    console.error('Failed to load Arabic font:', error);
    return null;
  }
};

/**
 * Checks whether a given string contains Arabic characters.
 * @param {any} text - The input value to check.
 * @returns {boolean} True if Arabic characters are detected, false otherwise.
 */
const containsArabicText = (text: any): boolean => {
  if (typeof text !== 'string') {
    return false;
  }
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text);
};

/**
 * change the , value to the csv value
 * @param {any} value - The input value.
 * @returns {any} The formatted string or original value.
 */
const escapeCsvValue = (value: any): any => {
  // Convert the value to a string, wrap it in quotes if it contains commas or quotes
  if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
    return `"${value.replace(/"/g, '""')}"`; // Escape quotes by doubling them
  }
  return value;
};

/**
 * Export the CSV file
 * @param {string[]} headers - The array of headers.
 * @param {any[][]} rows - The 2D array of rows.
 * @param {string} [fileName] - The filename for the exported CSV.
 */
export const exportCSV = (headers: string[], rows: any[][], fileName: string = 'data.csv') => {
  const data = rows.map((row) => row.map((value) => escapeCsvValue(value)));
  const csvContent = [headers, ...data].map((event_: any) => event_.join(',')).join('\n');

  // Create a Blob from the CSV content with UTF-8 BOM
  const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  // Create a link and trigger the download
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', fileName); // Set the filename
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link); // Cleanup
};

/**
 * Styling and layout definitions for an individual table column in PDF export.
 */
export interface ExportPdfColumnStyle {
  /** Explicit column width in points (pt), or auto/wrap calculation */
  cellWidth?: number | 'auto' | 'wrap';
  /** Minimum cell width in points (pt) */
  minCellWidth?: number;
  /** Maximum cell width in points (pt) */
  maxCellWidth?: number;
  /** Horizontal alignment for cells in this column */
  halign?: 'left' | 'center' | 'right' | 'justify';
  /** Vertical alignment for cells in this column */
  valign?: 'top' | 'middle' | 'bottom';
  /** Font size in points */
  fontSize?: number;
  /** Font style for cells in this column */
  fontStyle?: 'normal' | 'bold' | 'italic';
  /** Text overflow handling */
  overflow?: 'linebreak' | 'ellipsize' | 'visible' | 'hidden';
  /** Cell padding in points */
  cellPadding?: number | { top?: number; right?: number; bottom?: number; left?: number };
  /** Background fill color as RGB tuple [r, g, b] or hex string */
  fillColor?: [number, number, number] | string;
  /** Text color as RGB tuple [r, g, b] or hex string */
  textColor?: [number, number, number] | string;
  /** Additional custom autoTable cell styles */
  [key: string]: any;
}

/**
 * Custom styling and layout options for PDF export
 */
export interface ExportPdfOptions {
  /** Column-specific styling rules keyed by 0-based column index or matching header title */
  columnStyles?: Record<number | string, ExportPdfColumnStyle>;
  /** Header-specific styling rules */
  headStyles?: Record<string, any>;
  /** General table body and cell styling rules */
  styles?: Record<string, any>;
  /** Whether the document layout direction is right-to-left */
  isRtl?: boolean;
}

/**
 * Resolves column styles mapped by either numeric column index or header title to index-keyed styles.
 * @param {string[]} headers - The array of table headers.
 * @param {Record<number | string, ExportPdfColumnStyle>} [columnStyles] - The raw column styles dictionary.
 * @returns {Record<number, ExportPdfColumnStyle> | undefined} Normalized column styles keyed by column index.
 */
export const resolveColumnStyles = (
  headers: string[],
  columnStyles?: Record<number | string, ExportPdfColumnStyle>,
): Record<number, ExportPdfColumnStyle> | undefined => {
  if (!columnStyles) {
    return undefined;
  }

  const resolvedStyles: Record<number, ExportPdfColumnStyle> = {};

  Object.entries(columnStyles).forEach(([key, style]) => {
    const numericIndex = Number(key);
    if (!Number.isNaN(numericIndex)) {
      resolvedStyles[numericIndex] = {
        ...(resolvedStyles[numericIndex] || {}),
        ...style,
      };
      return;
    }

    const normalizedKey = key.trim().toLowerCase();
    const foundIndex = headers.findIndex((header) => {
      if (typeof header !== 'string') {
        return false;
      }
      return header.trim().toLowerCase() === normalizedKey;
    });

    if (foundIndex !== -1) {
      resolvedStyles[foundIndex] = {
        ...(resolvedStyles[foundIndex] || {}),
        ...style,
      };
    }
  });

  return resolvedStyles;
};

/**
 * Export table data to a PDF file with Arabic font support
 * @param {string[]} headers - The array of table headers.
 * @param {any[][]} rows - The 2D array of table row data.
 * @param {string} [fileName] - The name of the downloaded PDF file.
 * @param {boolean} [isArabic] - Whether Arabic font rendering should be applied.
 * @param {ExportPdfOptions} [options] - Additional styling and configuration options for table export.
 * @returns {Promise<void>} Resolves when export is complete.
 */
export const exportPDF = async (
  headers: string[],
  rows: any[][],
  fileName: string = 'generated.pdf',
  isArabic: boolean = false,
  options?: ExportPdfOptions,
): Promise<void> => {
  const document = new JsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  });

  const shouldApplyArabic =
    isArabic ||
    headers.some(containsArabicText) ||
    rows.some((row) => row.some(containsArabicText));

  let activeFont = 'helvetica';

  if (shouldApplyArabic) {
    const fontBase64 = await getAmiriFontBase64();
    if (fontBase64) {
      document.addFileToVFS('Amiri-Regular.ttf', fontBase64);
      document.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
      document.setFont('Amiri');
      activeFont = 'Amiri';
    }
  }

  /**
   * Processing arabic text for pdf
   * @param {any} text - The text to process
   * @returns {string} The processed text
   */
  const processText = (text: any): string => {
    const textString = text !== null && text !== undefined ? String(text) : '';
    if (
      shouldApplyArabic &&
      containsArabicText(textString) &&
      typeof (document as any).processArabic === 'function'
    ) {
      return (document as any).processArabic(textString);
    }
    return textString;
  };

  const processedHeaders = headers.map(processText);
  const processedRows = rows.map((row) => row.map(processText));
  const isRtl = isArabic || Boolean(options?.isRtl);
  const resolvedColumnStyles = resolveColumnStyles(headers, options?.columnStyles);

  autoTable(document, {
    head: [processedHeaders],
    body: processedRows,
    styles: {
      font: activeFont,
      fontStyle: 'normal',
      fontSize: 6,
      cellPadding: 2,
      lineWidth: 0.2, // Thin borders
      lineColor: '#000',
      halign: isRtl ? 'right' : 'center',
      valign: 'middle',
      minCellWidth: 25,
      overflow: 'linebreak',
      ...options?.styles,
    },
    headStyles: {
      font: activeFont,
      fontStyle: 'normal',
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineWidth: 0.2,
      fontSize: 6,
      halign: isRtl ? 'right' : 'center',
      valign: 'middle',
      ...options?.headStyles,
    },
    columnStyles: resolvedColumnStyles,
    tableLineWidth: 0.2,
    tableLineColor: '#000',
    margin: { top: 30, left: 10, right: 10 },
    theme: 'grid',
    startY: 20,
    /**
     * Dynamically taking page size
     * @param {any} data - The data object containing doc and pageNumber
     * @param {any} data.doc - The PDF document instance
     * @param {any} data.pageNumber - The current page number
     */
    didDrawPage: ({ doc, pageNumber }) => {
      const { width, height } = doc.internal.pageSize;
      doc.setFont(activeFont);
      doc.setFontSize(8);
      doc.text(`Page ${pageNumber}`, width - 50, height - 10);
    },
  });

  document.save(fileName);
};
